import React, { useState, useEffect, useRef } from 'react'
import { useSearchParams, useNavigate } from 'react-router-dom'
import { motion } from 'framer-motion'
import { supabase } from '@/integrations/supabase/client'
import { useAuth } from '@/contexts/AuthContext'
import { CheckCircle2, XCircle, ArrowRight, Loader2, ShieldCheck, LogIn, AlertCircle } from 'lucide-react'
import SEO from '../components/layout/SEO'

export default function TransferPage() {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const token = searchParams.get('token')
  const actionParam = searchParams.get('action') as 'accept' | 'decline' | null

  const { user, isLoading: authLoading, refreshUserProfile } = useAuth()

  const [loading, setLoading] = useState(false)
  const [loadingLabel, setLoadingLabel] = useState<string>('')
  const [result, setResult] = useState<{
    success: boolean
    action: 'accepted' | 'declined'
    message: string
    redirect_url?: string
  } | null>(null)
  const [error, setError] = useState<string | null>(null)

  const processedRef = useRef(false)

  const handleAction = async (action: 'accept' | 'decline') => {
    if (!token) {
      setError('Missing transfer token. Please use the link provided in your invitation email.')
      return
    }

    setLoading(true)
    setLoadingLabel(action === 'accept' ? 'Approving transfer and updating records…' : 'Declining transfer request…')
    setError(null)

    try {
      const { data, error: invokeErr } = await supabase.functions.invoke('confirm-facility-transfer', {
        body: { token, action },
      })

      if (invokeErr) {
        let errMsg = invokeErr.message
        try {
          if ((invokeErr as any).context) {
            const ctx = (invokeErr as any).context
            const json = typeof ctx.json === 'function' ? await ctx.json() : ctx
            if (json?.error) errMsg = json.error
          }
        } catch { /* ignore */ }
        throw new Error(errMsg || 'Failed to process transfer.')
      }

      if (data?.error) {
        throw new Error(data.error)
      }

      setResult(data)

      if (action === 'accept' && data?.redirect_url) {
        try {
          // Stash for post-login return path persistence if user is currently signed out
          localStorage.setItem('sage_auth_return_path', data.redirect_url)
        } catch { /* ignore */ }
      }
    } catch (err: any) {
      setError(err.message || 'An unexpected error occurred.')
    } finally {
      setLoading(false)
    }
  }

  // 1. Auto-read approve/decline action on return from email link (?token=...&action=accept|decline)
  useEffect(() => {
    if (!token) return
    if (actionParam !== 'accept' && actionParam !== 'decline') return
    if (processedRef.current) return

    processedRef.current = true
    handleAction(actionParam)
  }, [token, actionParam])

  // 2. Post-accept auto-redirect when user is signed in
  useEffect(() => {
    if (!result || result.action !== 'accepted' || !result.redirect_url) return

    // If user is currently signed in, refresh profile cache and redirect to new portal
    if (user && !authLoading) {
      refreshUserProfile?.().catch(() => {})
      const timer = setTimeout(() => {
        if (result.redirect_url!.startsWith('http')) {
          window.location.href = result.redirect_url!
        } else {
          window.location.href = window.location.origin + result.redirect_url!
        }
      }, 2400)
      return () => clearTimeout(timer)
    }
  }, [result, user, authLoading, refreshUserProfile])

  // Helper for computing sign-in destination for signed-out users
  const getSignInDestination = () => {
    const rawDest = result?.redirect_url || '/learn'
    try {
      if (rawDest.startsWith('http')) {
        const u = new URL(rawDest)
        return `${u.origin}/signin?returnPath=${encodeURIComponent(u.pathname + u.search)}`
      }
    } catch { /* ignore */ }
    return `/signin?returnPath=${encodeURIComponent(rawDest)}`
  }

  const isAlreadyAccepted = error?.toLowerCase().includes('already been accepted')
  const isAlreadyDeclined = error?.toLowerCase().includes('declined')
  const isExpired = error?.toLowerCase().includes('expired')

  return (
    <div
      style={{
        minHeight: '100vh',
        background: '#06060c',
        color: '#f8fafc',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '24px 16px',
        fontFamily: "'Inter', system-ui, sans-serif",
      }}
    >
      <SEO title="Facility Transfer Confirmation : Still Waters Academy" noindex />

      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
        style={{
          maxWidth: 520,
          width: '100%',
          background: '#0d1117',
          border: '1px solid rgba(255,255,255,0.1)',
          borderRadius: 24,
          padding: '36px 32px',
          boxShadow: '0 24px 64px rgba(0,0,0,0.6)',
        }}
      >
        {/* Emblem */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 24 }}>
          <div
            style={{
              width: 48,
              height: 48,
              borderRadius: 14,
              background: 'rgba(212,168,67,0.15)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <ShieldCheck size={26} color="#d4a843" />
          </div>
          <div>
            <h1 style={{ fontSize: 18, fontWeight: 800, margin: 0, color: '#f8fafc' }}>
              Still Waters Academy
            </h1>
            <p style={{ fontSize: 12, color: 'rgba(255,255,255,0.5)', margin: 0 }}>
              Official Organisation Transfer
            </p>
          </div>
        </div>

        {/* State 1: Active Loading (Processing transfer hook or manual click) */}
        {loading && (
          <div style={{ textAlign: 'center', padding: '24px 0' }}>
            <Loader2
              size={44}
              color="#0ea5e9"
              style={{ animation: 'spin 1s linear infinite', margin: '0 auto 16px', display: 'block' }}
            />
            <h2 style={{ fontSize: 20, fontWeight: 800, color: '#fff', margin: '0 0 8px' }}>
              {loadingLabel || 'Processing Transfer…'}
            </h2>
            <p style={{ fontSize: 14, color: 'rgba(255,255,255,0.6)', margin: 0 }}>
              Updating your organisation records in the database…
            </p>
          </div>
        )}

        {/* State 2: Completed Result (Accepted or Declined) */}
        {!loading && result && (
          <div>
            <div
              style={{
                width: 60,
                height: 60,
                borderRadius: 20,
                background: result.action === 'accepted' ? 'rgba(34,197,94,0.15)' : 'rgba(239,68,68,0.15)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '16px auto 20px',
              }}
            >
              {result.action === 'accepted' ? (
                <CheckCircle2 size={32} color="#22c55e" />
              ) : (
                <XCircle size={32} color="#ef4444" />
              )}
            </div>

            <h2
              style={{
                fontSize: 20,
                fontWeight: 800,
                color: '#fff',
                textAlign: 'center',
                margin: '0 0 10px',
              }}
            >
              {result.action === 'accepted' ? 'Transfer Completed' : 'Transfer Declined'}
            </h2>

            <p
              style={{
                fontSize: 14,
                color: 'rgba(255,255,255,0.7)',
                textAlign: 'center',
                lineHeight: 1.6,
                margin: '0 0 24px',
              }}
            >
              {result.message}
            </p>

            {/* If Accepted */}
            {result.action === 'accepted' && (
              <div>
                {user ? (
                  /* Signed-in user: auto-redirecting or instant button */
                  <div style={{ textAlign: 'center' }}>
                    <p style={{ fontSize: 12, color: '#d4a843', fontWeight: 600, margin: '0 0 12px' }}>
                      Redirecting to your new portal in a moment…
                    </p>
                    <button
                      onClick={() => {
                        if (result.redirect_url?.startsWith('http')) {
                          window.location.href = result.redirect_url
                        } else {
                          window.location.href = window.location.origin + (result.redirect_url || '/learn')
                        }
                      }}
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 8,
                        background: '#0ea5e9',
                        color: '#fff',
                        padding: '12px 24px',
                        borderRadius: 12,
                        border: 'none',
                        textDecoration: 'none',
                        fontWeight: 700,
                        fontSize: 14,
                        cursor: 'pointer',
                        width: '100%',
                      }}
                    >
                      Enter Portal Now <ArrowRight size={16} />
                    </button>
                  </div>
                ) : (
                  /* Signed-out user: prompt them to sign in */
                  <div style={{ textAlign: 'center' }}>
                    <div
                      style={{
                        background: 'rgba(14,165,233,0.1)',
                        border: '1px solid rgba(14,165,233,0.25)',
                        borderRadius: 12,
                        padding: '14px',
                        marginBottom: 16,
                        fontSize: 13,
                        color: 'rgba(255,255,255,0.8)',
                        lineHeight: 1.5,
                      }}
                    >
                      Your account transfer has been updated in the database. Please sign in to access your new organisation portal.
                    </div>
                    <button
                      onClick={() => {
                        window.location.href = getSignInDestination()
                      }}
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 8,
                        background: '#0ea5e9',
                        color: '#fff',
                        padding: '12px 24px',
                        borderRadius: 12,
                        border: 'none',
                        textDecoration: 'none',
                        fontWeight: 700,
                        fontSize: 14,
                        cursor: 'pointer',
                        width: '100%',
                      }}
                    >
                      <LogIn size={16} /> Sign In to Access Portal
                    </button>
                  </div>
                )}
              </div>
            )}

            {/* If Declined */}
            {result.action === 'declined' && (
              <div style={{ textAlign: 'center' }}>
                <button
                  onClick={() => navigate('/')}
                  style={{
                    padding: '12px 24px',
                    borderRadius: 12,
                    border: '1px solid rgba(255,255,255,0.15)',
                    background: 'transparent',
                    color: 'rgba(255,255,255,0.7)',
                    fontWeight: 700,
                    fontSize: 14,
                    cursor: 'pointer',
                    width: '100%',
                  }}
                >
                  Return to Home
                </button>
              </div>
            )}
          </div>
        )}

        {/* State 3: Already Completed (Previously Accepted) */}
        {!loading && !result && isAlreadyAccepted && (
          <div style={{ textAlign: 'center' }}>
            <div
              style={{
                width: 60,
                height: 60,
                borderRadius: 20,
                background: 'rgba(34,197,94,0.15)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '16px auto 20px',
              }}
            >
              <CheckCircle2 size={32} color="#22c55e" />
            </div>

            <h2 style={{ fontSize: 20, fontWeight: 800, color: '#fff', margin: '0 0 10px' }}>
              Transfer Already Completed
            </h2>

            <p style={{ fontSize: 14, color: 'rgba(255,255,255,0.7)', lineHeight: 1.6, margin: '0 0 24px' }}>
              This transfer invitation was already accepted and your account has moved to the new organisation.
            </p>

            <button
              onClick={() => {
                if (user) {
                  navigate('/learn')
                } else {
                  navigate('/signin')
                }
              }}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 8,
                background: '#0ea5e9',
                color: '#fff',
                padding: '12px 24px',
                borderRadius: 12,
                border: 'none',
                fontWeight: 700,
                fontSize: 14,
                cursor: 'pointer',
                width: '100%',
              }}
            >
              {user ? 'Enter Portal' : 'Sign In to Portal'} <ArrowRight size={16} />
            </button>
          </div>
        )}

        {/* State 4: Already Declined */}
        {!loading && !result && isAlreadyDeclined && (
          <div style={{ textAlign: 'center' }}>
            <div
              style={{
                width: 60,
                height: 60,
                borderRadius: 20,
                background: 'rgba(239,68,68,0.15)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '16px auto 20px',
              }}
            >
              <XCircle size={32} color="#ef4444" />
            </div>

            <h2 style={{ fontSize: 20, fontWeight: 800, color: '#fff', margin: '0 0 10px' }}>
              Transfer Was Declined
            </h2>

            <p style={{ fontSize: 14, color: 'rgba(255,255,255,0.7)', lineHeight: 1.6, margin: '0 0 24px' }}>
              This transfer request was declined. Your previous organisation membership remains completely unchanged.
            </p>

            <button
              onClick={() => navigate('/')}
              style={{
                padding: '12px 24px',
                borderRadius: 12,
                border: '1px solid rgba(255,255,255,0.15)',
                background: 'transparent',
                color: 'rgba(255,255,255,0.7)',
                fontWeight: 700,
                fontSize: 14,
                cursor: 'pointer',
                width: '100%',
              }}
            >
              Return to Home
            </button>
          </div>
        )}

        {/* State 5: Link Expired */}
        {!loading && !result && isExpired && (
          <div style={{ textAlign: 'center' }}>
            <div
              style={{
                width: 60,
                height: 60,
                borderRadius: 20,
                background: 'rgba(245,158,11,0.15)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '16px auto 20px',
              }}
            >
              <AlertCircle size={32} color="#f59e0b" />
            </div>

            <h2 style={{ fontSize: 20, fontWeight: 800, color: '#fff', margin: '0 0 10px' }}>
              Transfer Link Expired
            </h2>

            <p style={{ fontSize: 14, color: 'rgba(255,255,255,0.7)', lineHeight: 1.6, margin: '0 0 24px' }}>
              This transfer link has expired (links are valid for 7 days). Please contact the organisation administrator to send a new invitation.
            </p>

            <button
              onClick={() => navigate('/')}
              style={{
                padding: '12px 24px',
                borderRadius: 12,
                border: '1px solid rgba(255,255,255,0.15)',
                background: 'transparent',
                color: 'rgba(255,255,255,0.7)',
                fontWeight: 700,
                fontSize: 14,
                cursor: 'pointer',
                width: '100%',
              }}
            >
              Return to Home
            </button>
          </div>
        )}

        {/* State 6: Generic Error (not already accepted/declined/expired) */}
        {!loading && !result && error && !isAlreadyAccepted && !isAlreadyDeclined && !isExpired && (
          <div style={{ marginBottom: 20 }}>
            <div
              style={{
                padding: '14px 16px',
                borderRadius: 12,
                background: 'rgba(239,68,68,0.1)',
                border: '1px solid rgba(239,68,68,0.3)',
                color: '#fca5a5',
                fontSize: 13,
                lineHeight: 1.5,
                marginBottom: 16,
              }}
            >
              {error}
            </div>

            <button
              onClick={() => navigate('/')}
              style={{
                padding: '12px 24px',
                borderRadius: 12,
                border: '1px solid rgba(255,255,255,0.15)',
                background: 'transparent',
                color: 'rgba(255,255,255,0.7)',
                fontWeight: 700,
                fontSize: 14,
                cursor: 'pointer',
                width: '100%',
              }}
            >
              Return to Home
            </button>
          </div>
        )}

        {/* State 7: Interactive Decision (When user opened /transfer?token=... directly without action param) */}
        {!loading && !result && !error && (
          <div>
            <h2 style={{ fontSize: 20, fontWeight: 800, color: '#fff', margin: '0 0 10px' }}>
              Confirm Organisation Transfer
            </h2>

            <p style={{ fontSize: 14, color: 'rgba(255,255,255,0.7)', lineHeight: 1.65, margin: '0 0 20px' }}>
              You have received an invitation to transfer your learning account to another organisation on Still Waters Academy.
            </p>

            <div
              style={{
                background: 'rgba(212,168,67,0.06)',
                border: '1px solid rgba(212,168,67,0.2)',
                borderRadius: 14,
                padding: '16px',
                marginBottom: 24,
              }}
            >
              <h3 style={{ fontSize: 13, fontWeight: 700, color: '#d4a843', margin: '0 0 6px' }}>
                What happens when you accept?
              </h3>
              <ul style={{ margin: 0, paddingLeft: 18, color: 'rgba(255,255,255,0.7)', fontSize: 13, lineHeight: 1.6 }}>
                <li>Your active account moves immediately to the new organisation.</li>
                <li>Your existing achievements, certificates, and evidence from your previous organisation remain permanently recorded there.</li>
                <li>If you decline, your current organisation membership remains completely unchanged.</li>
              </ul>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              <button
                onClick={() => handleAction('accept')}
                disabled={loading || !token}
                style={{
                  width: '100%',
                  padding: '14px',
                  borderRadius: 12,
                  border: 'none',
                  background: '#0ea5e9',
                  color: '#fff',
                  fontWeight: 800,
                  fontSize: 14,
                  cursor: loading || !token ? 'not-allowed' : 'pointer',
                  opacity: loading || !token ? 0.6 : 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 8,
                }}
              >
                <CheckCircle2 size={16} /> ✅ Accept Transfer & Move Account
              </button>

              <button
                onClick={() => handleAction('decline')}
                disabled={loading || !token}
                style={{
                  width: '100%',
                  padding: '12px',
                  borderRadius: 12,
                  border: '1px solid rgba(255,255,255,0.12)',
                  background: 'transparent',
                  color: 'rgba(255,255,255,0.6)',
                  fontWeight: 700,
                  fontSize: 13,
                  cursor: loading || !token ? 'not-allowed' : 'pointer',
                }}
              >
                ❌ Decline Transfer
              </button>
            </div>
          </div>
        )}
      </motion.div>
    </div>
  )
}
